/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Helperclass voor het werken met Cytoscape in Dynalearn.
 *
 * Gegenereerd met Junie (AI), 17-03-2026
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 */
import type { Collection, Core, NodeSingular } from 'cytoscape';
import { elements } from 'dynalearn-common/lib/elements';

export class Dynascape {
    /**
     * De cytoscape-instance.
     */
    private readonly cy: Core;

    constructor(cy: Core) {
        this.cy = cy;
    }

    /**
     * Zoekt een node-instantie op basis van ID of de node zelf.
     * @param node Het ID van de node of de node-instantie zelf.
     * @returns De node-instantie of undefined als deze niet bestaat.
     */
    getNode(node: string | NodeSingular): NodeSingular | undefined {
        const nodeInstance = typeof node === 'string' ? this.cy.getElementById(node) : node;
        return nodeInstance.empty() ? undefined : nodeInstance;
    }

    /**
     * Zoekt de naam van een node op.
     * @param node Het ID van de node of de node-instantie zelf.
     * @param handleZero Of een node met 'isZero' op true als "Ø" moet worden teruggegeven.
     * @returns De naam van de node in kleine letters (of "Ø").
     */
    nodeName(node: string | NodeSingular, handleZero: boolean = false): string {
        const nodeInstance = this.getNode(node);
        if (!nodeInstance) return '?';
        if (handleZero && nodeInstance.data('isZero')) {
            return 'Ø';
        }
        const naam = nodeInstance.data('label') || nodeInstance.data('naam') || nodeInstance.data('name') || '?';
        return naam.toLowerCase();
    }

    /**
     * Haalt alle nodes op van een specifiek type, inclusief subtypen.
     * Filtert optioneel op condtag en condstate.
     * Als condtag null is, worden nodes MET een condtag genegeerd (de "normale" situatie).
     * Als condtag is meegegeven, worden alleen nodes met die condtag EN condstate teruggegeven.
     * Als condtag is weggelaten (undefined), vindt er geen filtering op condtag plaats.
     * @param type De typenaam.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel, "cond" of "cons").
     * @returns Een collectie van gevonden nodes.
     */
    typeNodes(type: string, condtag?: string | null, condstate?: 'cond' | 'cons' | null): Collection {
        const types = [type, ...elements.alleSubs(type)];
        const typeSelector = types.map(t => `[type="${t}"]`).join(', ');
        let nodes = this.cy.$(typeSelector);

        if (condtag === null) {
            // Expliciet null: negeer nodes die een condtag hebben
            nodes = nodes.filter(n => !n.data('condtag'));
        } else if (condtag && condstate) {
            // Wel condtag/condstate meegegeven: filter op exact die combinatie
            nodes = nodes.filter(n => n.data('condtag') === condtag && n.data('condstate') === condstate);
        }

        return nodes;
    }

    /**
     * Controleert of een node een relatie is.
     * @param node De node-instantie.
     */
    isRelation(node: NodeSingular): boolean {
        return elements.isRelation(node.data('type'));
    }

    /**
     * Onze versie van outgoers, houdt rekening met edgeHandles.
     * @param node De bron-node (ID of instantie).
     * @param selector Optionele selector voor de doelelementen.
     */
    outElements(node: string | NodeSingular, selector?: string): Collection {
        const nodeInstance = this.getNode(node);
        if (!nodeInstance) return this.cy.collection();

        let outg = this.cy.collection();
        // 1: onze "uitgangen": het element zelf en zijn edgehandles
        const uitgangen = nodeInstance.add(`[type="edgeHandle"][handleParent="${nodeInstance.id()}"]`);
        
        // 2: directe overkanten: de outgoers van onze uitgangen
        outg = outg.add(uitgangen.outgoers('[type!="edgeHandle"]'));
        
        // 3: overkanten met edgehandles
        uitgangen.outgoers('[type="edgeHandle"]').forEach((otherHandle) => {
            const parentId = otherHandle.data('handleParent');
            if (parentId) {
                outg = outg.add(this.cy.getElementById(parentId));
            }
        });

        return selector ? outg.filter(selector) : outg;
    }

    /**
     * Onze versie van incomers, houdt rekening met edgeHandles.
     * @param node De doel-node (ID of instantie).
     * @param selector Optionele selector voor de bronelementen.
     */
    inElements(node: string | NodeSingular, selector?: string): Collection {
        const nodeInstance = this.getNode(node);
        if (!nodeInstance) return this.cy.collection();

        let inc = this.cy.collection();
        // 1: onze "uitgangen": het element zelf en zijn edgehandles
        const uitgangen = nodeInstance.add(`[type="edgeHandle"][handleParent="${nodeInstance.id()}"]`);
        
        // 2: directe overkanten: de incomers van onze uitgangen
        inc = inc.add(uitgangen.incomers('[type!="edgeHandle"]'));
        
        // 3: overkanten met edgehandles
        uitgangen.incomers('[type="edgeHandle"]').forEach((otherHandle) => {
            const parentId = otherHandle.data('handleParent');
            if (parentId) {
                inc = inc.add(this.cy.getElementById(parentId));
            }
        });

        return selector ? inc.filter(selector) : inc;
    }

    /**
     * Geeft de parent-node terug van de opgegeven node op basis van het parentId-veld.
     * @param node Het ID van de node of de node-instantie zelf.
     * @returns De parent-node of undefined als deze niet gevonden is.
     */
    getParent(node: string | NodeSingular): NodeSingular | undefined {
        const nodeInstance = this.getNode(node);
        if (!nodeInstance) return undefined;
        const parentId = nodeInstance.data('parentId');
        if (!parentId) return undefined;
        
        const parent = this.cy.getElementById(parentId);
        return parent.empty() ? undefined : parent;
    }

    /**
     * Geeft de quantityspace-node terug voor een quantity-node.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns De quantityspace-node of undefined als deze niet gevonden is.
     */
    getQS(node: string | NodeSingular): NodeSingular | undefined {
        const qs = this.outElements(node, '[type="quantity_space"]');
        return qs.empty() ? undefined : qs.first() as NodeSingular;
    }

    /**
     * Geeft de derivative-node terug voor een quantity-node.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns De derivative-node of undefined als deze niet gevonden is.
     */
    getDQS(node: string | NodeSingular): NodeSingular | undefined {
        const dqs = this.outElements(node, '[type="derivative"]');
        return dqs.empty() ? undefined : dqs.first() as NodeSingular;
    }

    /**
     * Zoekt de grootheden die verbonden zijn via een correspondence_qs node.
     * @param node De correspondence_qs node.
     * @returns Een object met de twee quantity nodes (q1 en q2) of undefined als ze niet beide gevonden worden.
     */
    getQuantitiesForCorrespondenceQS(node: NodeSingular): { q1: NodeSingular, q2: NodeSingular } | undefined {
        const fromId = node.data('from');
        const toId = node.data('to');

        if (!fromId || !toId) return undefined;

        const qs1 = this.cy.getElementById(fromId);
        const qs2 = this.cy.getElementById(toId);

        if (qs1.empty() || qs2.empty()) return undefined;

        const q1 = this.inElements(qs1 as NodeSingular, '[type="quantity"]').first();
        const q2 = this.inElements(qs2 as NodeSingular, '[type="quantity"]').first();

        if (q1.empty() || q2.empty()) return undefined;

        return { q1: q1 as NodeSingular, q2: q2 as NodeSingular };
    }

    /**
     * Zoekt de grootheden die verbonden zijn via een correspondence_dqs node.
     * @param node De correspondence_dqs node.
     * @returns Een object met de twee quantity nodes (q1 en q2) of undefined als ze niet beide gevonden worden.
     */
    getQuantitiesForCorrespondenceDQS(node: NodeSingular): { q1: NodeSingular, q2: NodeSingular } | undefined {
        const fromId = node.data('from');
        const toId = node.data('to');

        if (!fromId || !toId) return undefined;

        const d1 = this.cy.getElementById(fromId);
        const d2 = this.cy.getElementById(toId);

        if (d1.empty() || d2.empty()) return undefined;

        const q1 = this.inElements(d1 as NodeSingular, '[type="quantity"]').first();
        const q2 = this.inElements(d2 as NodeSingular, '[type="quantity"]').first();

        if (q1.empty() || q2.empty()) return undefined;

        return { q1: q1 as NodeSingular, q2: q2 as NodeSingular };
    }

    /**
     * Geeft alle waarden in een quantityspace terug, van laag naar hoog.
     * De keten loopt vanaf de qsNode (hoogste waarde) naar beneden.
     * @param qsNode Het ID van de quantityspace-node of de node-instantie zelf.
     * @returns Een array van node-instanties van de waarden, gesorteerd van laag naar hoog.
     */
    getQSValues(qsNode: string | NodeSingular): NodeSingular[] {
        const qsNodeInstance = this.getNode(qsNode);
        if (!qsNodeInstance) {
            return [];
        }

        // We bouwen de lijst op door de keten van uitgaande relaties te volgen (van hoog naar laag)
        const result: NodeSingular[] = [];
        let current = this.outElements(qsNodeInstance, '[type="quantity_space_interval"],[type="quantity_space_point"]');
        
        while (!current.empty()) {
            const nextNode = current.first() as NodeSingular;
            result.push(nextNode);
            current = this.outElements(nextNode, '[type="quantity_space_interval"],[type="quantity_space_point"]');
        }

        // De keten loopt van boven naar beneden, we willen van laag naar hoog
        return result.reverse();
    }

    /**
     * Zoekt de quantity node die bij een bepaalde quantity space waarde hoort.
     * @param valueNode De node van een waarde in de quantity space (interval of punt).
     * @returns De bijbehorende quantity node of undefined.
     */
    getQuantityForQSValue(valueNode: NodeSingular): NodeSingular | undefined {
        // De waarde zit in een quantity_space, die verbonden is aan een quantity via Dynascape.
        // We zoeken de parent-node die een quantity_space is (kan meerdere niveaus diep zitten).
        const qs = this.zoekUp(valueNode, '[type="quantity_space"]');
        if (!qs) return undefined;

        // Nu zoeken we de inkomende quantity node voor de quantity_space node via de Dynascape relatie.
        const q = this.inElements(qs, '[type="quantity"]').first();
        return q.empty() ? undefined : q as NodeSingular;
    }

    /**
     * Zoekt de quantity node die bij een bepaalde derivative space waarde hoort.
     * @param valueNode De node van een waarde in de derivative space (derivative_element).
     * @returns De bijbehorende quantity node of undefined.
     */
    getQuantityForDValue(valueNode: NodeSingular): NodeSingular | undefined {
        // De waarde zit in een derivative, die verbonden is aan een quantity via Dynascape.
        // We zoeken de parent-node die een derivative is.
        const dqs = this.zoekUp(valueNode, '[type="derivative"]');
        if (!dqs) return undefined;

        // Nu zoeken we de inkomende quantity node voor de derivative node via de Dynascape relatie.
        const q = this.inElements(dqs, '[type="quantity"]').first();
        return q.empty() ? undefined : q as NodeSingular;
    }

    /**
     * Zoekt de argumenten die verbonden zijn via een relatie-node (bijv. ineq, correspondence, influence, proportionality).
     * @param node De relatie-node.
     * @returns Een object met de twee argument nodes (from en to) of undefined als ze niet beide gevonden worden.
     */
    getRelationArguments(node: NodeSingular): { from: NodeSingular, to: NodeSingular } | undefined {
        const fromId = node.data('from');
        const toId = node.data('to');

        if (!fromId || !toId) return undefined;

        const fromNode = this.cy.getElementById(fromId);
        const toNode = this.cy.getElementById(toId);

        if (fromNode.empty() || toNode.empty()) return undefined;

        return { from: fromNode as NodeSingular, to: toNode as NodeSingular };
    }

    /**
     * Zoekt recursief naar boven in de parent-hiërarchie tot een node wordt gevonden die voldoet aan de selector.
     * @param node De start-node.
     * @param selector De cytoscape-selector.
     * @returns De gevonden node of undefined.
     */
    zoekUp(node: string | NodeSingular, selector: string): NodeSingular | undefined {
        const current = this.getNode(node);
        if (!current) return undefined;

        const parent = this.getParent(current);
        if (!parent) return undefined;

        if (parent.is(selector)) {
            return parent;
        }

        return this.zoekUp(parent, selector);
    }

    /**
     * Geeft de drie mogelijke waarden van een derivative terug (min, zero, plus).
     * De keten loopt vanaf de dqsNode (plus) naar beneden (zero, min).
     * @param dqsNode Het ID van de derivative-node of de node-instantie zelf.
     * @returns Een array van node-instanties (derivative_min, derivative_zero, derivative_plus).
     */
    getDQSValues(dqsNode: string | NodeSingular): NodeSingular[] {
        const dqsNodeInstance = this.getNode(dqsNode);
        if (!dqsNodeInstance) {
            return [];
        }

        // De keten loopt van boven naar beneden: derivative -> plus -> zero -> min
        const result: NodeSingular[] = [];
        let current = this.outElements(dqsNodeInstance, '[type="derivative_plus"]');

        while (!current.empty()) {
            const nextNode = current.first() as NodeSingular;
            result.push(nextNode);
            current = this.outElements(nextNode, '[type="derivative_zero"],[type="derivative_min"]');
        }

        // We geven de waarden terug in de volgorde: min, zero, plus
        return result.reverse();
    }

    /**
     * Geeft de namen van de quantityspace-waarden terug, van laag naar hoog.
     * Een waarde met 'isZero' op true wordt teruggegeven als "Ø".
     * @param qsNode Het ID van de quantityspace-node of de node-instantie zelf.
     * @returns Een array van namen van de waarden, van laag naar hoog.
     */
    getQSValueNames(qsNode: string | NodeSingular): string[] {
        return this.getQSValues(qsNode).map(node => this.nodeName(node, true));
    }

    /**
     * Geeft de typen van de derivative-waarden terug (derivative_min, derivative_zero, derivative_plus).
     * @param dqsNode Het ID van de derivative-node of de node-instantie zelf.
     * @returns Een array van typen van de waarden.
     */
    getDQSValueTypes(dqsNode: string | NodeSingular): string[] {
        return this.getDQSValues(dqsNode).map(node => node.data('type'));
    }

    /**
     * Zoekt alle beginwaarde-nodes (quantity_value) voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns Een array van quantity_value nodes.
     */
    getQValueNodes(node: string | NodeSingular): NodeSingular[] {
        const result: NodeSingular[] = [];
        const qsNode = this.getQS(node);
        if (!qsNode) return result;

        const qsElements = this.getQSValues(qsNode);
        for (const qsEl of qsElements) {
            const valueNode = this.outElements(qsEl, '[type="quantity_value"]');
            if (valueNode.nonempty()) {
                result.push(valueNode.first() as NodeSingular);
            }
        }
        return result;
    }

    /**
     * Zoekt de beginwaarde node (quantity_value) voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns De quantity_value node, of undefined.
     * @deprecated Gebruik getQValueNodes voor situaties met meerdere waarden (bijv. conditiesets).
     */
    getQValueNode(node: string | NodeSingular): NodeSingular | undefined {
        return this.getQValueNodes(node)[0];
    }

    /**
     * Zoekt de beginwaarde (quantity_value) voor een quantity.
     * @param node Het ID van de quantity-node or de node-instantie zelf.
     * @returns De naam van het quantity_space_element waar de waarde aan hangt, of undefined.
     */
    getQValueName(node: string | NodeSingular): string | undefined {
        const valueNode = this.getQValueNode(node);
        if (valueNode) {
            // De naam komt van de parent (het quantity_space_element)
            const parent = this.inElements(valueNode).filter(n => n.isNode()).first() as NodeSingular;
            return parent ? this.nodeName(parent, true) : undefined;
        }
        return undefined;
    }

    /**
     * Zoekt alle afgeleide waarde nodes (derivative_value) voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns Een array van derivative_value nodes.
     */
    getDValueNodes(node: string | NodeSingular): NodeSingular[] {
        const result: NodeSingular[] = [];
        const dqsNode = this.getDQS(node);
        if (!dqsNode) return result;

        const dqsElements = this.getDQSValues(dqsNode);
        for (const dqsEl of dqsElements) {
            const valueNode = this.outElements(dqsEl, '[type="derivative_value"]');
            if (valueNode.nonempty()) {
                result.push(valueNode.first() as NodeSingular);
            }
        }
        return result;
    }

    /**
     * Zoekt de huidige afgeleide waarde node (derivative_value) voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns De derivative_value node, of undefined.
     * @deprecated Gebruik getDValueNodes voor situaties met meerdere waarden (bijv. conditiesets).
     */
    getDValueNode(node: string | NodeSingular): NodeSingular | undefined {
        return this.getDValueNodes(node)[0];
    }

    /**
     * Zoekt het type van de huidige afgeleide waarde (derivative_value) voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns Het type van het derivative_element waar de waarde aan hangt: "derivative_plus", "derivative_zero", "derivative_min", of undefined.
     */
    getDValueType(node: string | NodeSingular): string | undefined {
        const valueNode = this.getDValueNode(node);
        if (valueNode) {
            // Het type komt van de parent (het derivative_element)
            const parent = this.inElements(valueNode).filter(n => n.isNode()).first() as NodeSingular;
            return parent ? parent.data('type') : undefined;
        }
        return undefined;
    }

    /**
     * Zoekt de exogeen-node voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns De exogeen-node (subType van q_exo), of undefined.
     */
    getExoNode(node: string | NodeSingular): NodeSingular | undefined {
        const nodeInstance = this.getNode(node);
        if (!nodeInstance) return undefined;

        const exoNode = this.typeNodes('q_exo').intersect(this.outElements(nodeInstance));
        return exoNode.empty() ? undefined : exoNode.first() as NodeSingular;
    }

    /**
     * Zoekt het type van het exogeen-element voor een quantity.
     * @param node Het ID van de quantity-node of de node-instantie zelf.
     * @returns Het type van de exogeen-node (bijv. "q_exo_steady"), of undefined.
     */
    getExoType(node: string | NodeSingular): string | undefined {
        const exoNode = this.getExoNode(node);
        return exoNode ? exoNode.data('type') : undefined;
    }

    /**
     * Geeft de condtag (naam van de conditieset) terug als de node onderdeel is van een conditieset.
     * @param node Het ID van de node of de node-instantie zelf.
     * @returns De condtag of undefined.
     */
    inConditieset(node: string | NodeSingular): string | undefined {
        const nodeInstance = this.getNode(node);
        return nodeInstance?.data('condtag') || undefined;
    }

    /**
     * Controleert of een node (of een relevante gerelateerde node) voldoet aan de conditieset context.
     * @param node De node die gecheckt wordt.
     * @param targetCondtag De gezochte condtag (null voor normaal).
     * @param targetCondstate De gezochte condstate (cond/cons).
     * @param inherit Of we mogen overerven van de parent (standaard true).
     */
    match(node: NodeSingular | any, targetCondtag: string | null, targetCondstate?: 'cond' | 'cons' | null, inherit: boolean = true): boolean {
        let nodeTag = node.data('condtag');
        let nodeState = node.data('condstate');

        // Als de node zelf geen tag heeft, maar we mogen overerven, check de parent.
        if (!nodeTag && inherit) {
            const parent = this.inElements(node).filter(n => n.isNode() && !this.isRelation(n)).first() as NodeSingular;
            if (parent && parent.nonempty()) {
                nodeTag = parent.data('condtag');
                nodeState = parent.data('condstate');
            }
        }

        if (targetCondtag === null) {
            // In normale modus mogen nodes met een condtag niet meedoen.
            return !nodeTag;
        }

        // In conditieset modus moet de tag exact matchen en de state ook (als gevraagd).
        if (nodeTag !== targetCondtag) return false;
        return !(targetCondstate && nodeState !== targetCondstate);
    }

    /**
     * Geeft de condstate van de node terug ("cond", "cons" of undefined).
     * @param node Het ID van de node of de node-instantie zelf.
     * @returns De condstate of undefined.
     */
    conditieState(node: string | NodeSingular): 'cond' | 'cons' | undefined {
        const nodeInstance = this.getNode(node);
        const state = nodeInstance?.data('condstate');
        return (state === 'cond' || state === 'cons') ? state : undefined;
    }

    /**
     * Controleert of een type een subtype is van een ander type.
     * @param type De typenaam.
     * @param superType De supertypenaam.
     * @returns True als het type gelijk is aan of een subtype is van superType.
     */
    isSubtypeOf(type: string, superType: string): boolean {
        return elements.isSubtypeOf(type, superType);
    }

    /**
     * Geeft de ruwe cytoscape instance terug voor specialistische operaties.
     */
    get instance(): Core {
        return this.cy;
    }
}
